import React, { useState } from 'react'
import { Box, Typography, Stack, Avatar, useTheme, IconButton, Alert } from '@mui/material'
import { HiOutlinePlus } from 'react-icons/hi'
import { MdHistory } from 'react-icons/md'
import BottomSheet from '@components/BottomSheet'
import { formatNumber } from '@plug-core/utils/formatNumber'
import EmptyList from '@components/EmptyList'
import { useTranslation } from 'react-i18next'
import CanisterCard from './components/CanisterCard'
import { ROUTES } from '@utils/routes'
import { useNavigate } from 'react-router-dom'
import Prompt from '@components/Prompt'
import ApproveCycles from '@modules/ApproveCycles'
import CanisterForm from './components/CanisterForm'
import { HiMiniExclamationTriangle } from 'react-icons/hi2'
import Button from '@components/Button'
import useTopup from '@plug-core/hooks/useTopup'
import { CanisterInfo } from '@plug-core/idls/topUp/topUp'
import { Titlebar } from '@components/Titlebar/Titlebar'

export const CanisterTopupPage = () => {
  const [approveVisible, setApproveVisible] = useState(false)
  const [newCanisterFormVisible, setNewCanisterFormVisible] = useState(false)
  const [canisterInEdit, setCanisterInEdit] = useState<CanisterInfo>()

  const {
    profile,
    approvedBalance,
    walletCyclesBalance,
    wallet,
    loading,
    canisterInDelete,
    onCanisterDelete,
    deleteInProgress,
    refreshApproveBalance,
    setCanisterInDelete,
    addCanister,
    updateCanister,
  } = useTopup()

  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()

  const refreshApproveAndClose = () => {
    setApproveVisible(false)
    refreshApproveBalance()
  }

  const hideCanisterForm = () => {
    setNewCanisterFormVisible(false)
    setCanisterInEdit(undefined)
  }

  return (
    <>
      <Stack pt={2} pb={1} mx={1}>
        <Titlebar
          title={t('topup.title')}
          onActionClick={{
            type: 'back',
            action: () => navigate(-1),
          }}
          leftItem={
            <IconButton
              sx={{ width: 40 }}
              color="primary"
              onClick={() => {
                navigate(ROUTES.TOPUP_HISTORY, {
                  state: { principalId: wallet?.principalId },
                })
              }}
            >
              <MdHistory />
            </IconButton>
          }
        />
      </Stack>
      <Box px={2}>
        <Stack justifyContent="center" bgcolor="background.paper" py={2} borderRadius={2} mb={2}>
          <Typography textAlign="center">{t('topup.tcyclesBalance')}</Typography>
          <Stack alignItems="center" direction="row">
            <Stack justifyContent="center" flex={1} alignItems="center">
              <Typography variant="h2" mt={0.75} mb={0.125} fontSize={34}>
                {formatNumber(walletCyclesBalance?.balance || 0)}
              </Typography>
              <Typography color={(theme) => theme.palette.text.secondary}>
                {t('topup.plugBalance')}
              </Typography>
              <Button onClick={() => navigate(ROUTES.MINT_CYCLES)}>Mint</Button>
            </Stack>
            <Stack justifyContent="center" flex={1} alignItems="center">
              <Typography variant="h2" mt={0.75} mb={0.125} fontSize={34}>
                {formatNumber(approvedBalance)}
              </Typography>
              <Typography color={(theme) => theme.palette.text.secondary}>
                {t('topup.approved')}
              </Typography>
              <Button onClick={() => setApproveVisible(true)}>Add</Button>
            </Stack>
          </Stack>
        </Stack>
        <Button
          variant="outlined"
          onClick={() => navigate(ROUTES.SEND, { state: { topup: true } })}
          fullWidth
        >
          {t('topup.topupCanister')}
        </Button>

        {profile?.enough_cycles == false && (
          <Stack mt={2}>
            <Alert severity="info" sx={{ alignItems: 'center' }}>
              {t('topup.notEnoughBalance')}
            </Alert>
          </Stack>
        )}

        <Stack spacing={2} my={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('topup.savedCanisters')}</Typography>
            <Button startIcon={<HiOutlinePlus />} onClick={() => setNewCanisterFormVisible(true)}>
              {t('common.add')}
            </Button>
          </Stack>
          {!loading && !profile?.canisters?.length && (
            <EmptyList
              title={t('topup.noCanisters')}
              description={t('topup.noCanistersDescription')}
            />
          )}

          {profile?.canisters?.map((canister, i) => (
            <CanisterCard
              key={i}
              canister={canister}
              onCanisterDelete={() => setCanisterInDelete(canister)}
              onCanisterUpdate={() => setCanisterInEdit(canister)}
            />
          ))}
        </Stack>

        <Prompt show={approveVisible} close={() => setApproveVisible(false)}>
          <ApproveCycles hideSelf={refreshApproveAndClose} />
        </Prompt>

        <Prompt show={!!canisterInDelete}>
          <Stack alignItems="center">
            <Avatar sx={{ bgcolor: 'background.default', width: 50, height: 50 }}>
              <HiMiniExclamationTriangle color={theme.palette.rainbow.yellow} size={30} />
            </Avatar>
            <Stack alignItems="center">
              <Typography variant="h6" mt={0.2}>
                {t('topup.deleteWarning.title')}
              </Typography>
              <Typography textAlign="center" mt={1} mb={2} color="text.light">
                {t('topup.deleteWarning.description')}
              </Typography>
            </Stack>

            <Stack width="100%" spacing={1}>
              <Button
                fullWidth
                onClick={onCanisterDelete}
                variant="destroy"
                loading={deleteInProgress}
              >
                {t('common.confirm')}
              </Button>
              <Button fullWidth onClick={() => setCanisterInDelete(undefined)} variant="outlined">
                {t('common.cancel')}
              </Button>
            </Stack>
          </Stack>
        </Prompt>

        <BottomSheet
          visible={newCanisterFormVisible || !!canisterInEdit}
          onBackdropClick={hideCanisterForm}
        >
          <CanisterForm
            hideSelf={hideCanisterForm}
            newCanister={newCanisterFormVisible}
            addCanister={addCanister}
            updateCanister={updateCanister}
            canister={canisterInEdit}
          />
        </BottomSheet>
      </Box>
    </>
  )
}
